import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * EditorSidebarSection – lightweight grouping for sidebar blocks.
 *
 * Used to visually separate the canonical trio:
 *   A. Preview (visitor-facing teaser)
 *   B. Settings (status, visibility)
 *   C. Actions (save, cancel)
 *
 * Status hierarchy rule: status has ONE primary home in Settings.
 * Preview must remain visitor-facing — no admin badges.
 */

interface EditorSidebarSectionProps {
  /** Optional uppercase section heading (e.g. "Förhandsvisning", "Inställningar") */
  heading?: string;
  children: ReactNode;
  className?: string;
}

export function EditorSidebarSection({ heading, children, className }: EditorSidebarSectionProps) {
  return (
    <div className={cn('space-y-2.5', className)}>
      {heading && (
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground pl-0.5">
          {heading}
        </p>
      )}
      {children}
    </div>
  );
}

/**
 * EditorSidebarCard – bordered card wrapper for settings/controls.
 * Use for grouping related controls (status, publish state, etc).
 */
interface EditorSidebarCardProps {
  children: ReactNode;
  className?: string;
}

export function EditorSidebarCard({ children, className }: EditorSidebarCardProps) {
  return (
    <div className={cn('rounded-lg border border-border/40 bg-card p-4 space-y-3', className)}>
      {children}
    </div>
  );
}
